import { useState } from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';

export const ContactPage = ({ onRequestQuote }) => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch('/php/send-mail.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'contact',
          name: formData.name,
          company: formData.company,
          email: formData.email,
          message: formData.message,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Something went wrong');
      }
      setIsSubmitted(true);
      setFormData({
        name: '',
        company: '',
        email: '',
        message: '',
      });
    } catch (err) {
      alert(err.message || 'Failed to send. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F5F2ED] min-h-screen pt-24 md:pt-28 pb-16">
      {/* Hero / Intro */}
      <section className="pb-8 md:pb-10 leaf-pattern" id="home">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className=" text-xs tracking-[0.25em] uppercase text-[#5A5A5A] mb-3">
            Get in touch
          </p>
          <h1 className=" text-3xl sm:text-4xl lg:text-5xl text-[#2B2B2B] mb-4">
            Contact Us
          </h1>
          <p className=" text-sm sm:text-base text-[#5A5A5A] max-w-2xl">
            Share your requirements for eco-friendly bags, custom printing or bulk export programs.
            Our team will respond within 24 hours with tailored options.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="bg-[#F5F2ED]" id="contact">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Contact Details */}
            <div>
              <h2 className=" text-xl sm:text-2xl text-[#2B2B2B] mb-4">
                Global Export Enquiries
              </h2>
              <p className=" text-sm sm:text-base text-[#5A5A5A] mb-6">
                Bags4Ever supplies retailers, brands and promotional partners worldwide with
                durable, reusable bags made from jute, cotton and canvas. Reach out to discuss
                sampling, customisation and bulk orders.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#2F4F3E] mt-1 flex-shrink-0" />
                  <div>
                    <p className=" text-sm text-[#2B2B2B] font-semibold">
                      Bags4Ever Inc.
                    </p>
                    <p className=" text-sm text-[#5A5A5A]">
                      Nevada, USA
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-[#2F4F3E] flex-shrink-0" />
                  <a
                    href="mailto:info@bags4ever.com"
                    className="text-sm text-[#2F4F3E] hover:underline"
                  >
                    info@bags4ever.com
                  </a>
                </div>

                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-[#2F4F3E] flex-shrink-0" />
                  <a
                    href="tel:+12134712170"
                    className="text-sm text-[#2F4F3E] hover:underline"
                  >
                    +1 213-471-2170
                  </a>
                </div>

                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#2F4F3E] mt-1 flex-shrink-0" />
                  <div>
                    <p className=" text-sm text-[#2B2B2B] font-semibold">
                      India – Registered Office
                    </p>
                    <p className=" text-sm text-[#5A5A5A]">
                      5, Gujrawala Town - II,
                      <br />
                      Delhi - 110009, India
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#2F4F3E] mt-1 flex-shrink-0" />
                  <div>
                    <p className=" text-sm text-[#2B2B2B] font-semibold">
                      Manufacturing &amp; Sourcing Support
                    </p>
                    <p className=" text-sm text-[#5A5A5A]">
                      Reliable Exports – India
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-[#2F4F3E] flex-shrink-0" />
                  <a
                    href="tel:+919810272098"
                    className="text-sm text-[#2F4F3E] hover:underline"
                  >
                    +91 98102 72098
                  </a>
                </div>

                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-[#2F4F3E] flex-shrink-0" />
                  <a
                    href="mailto:sales@reliableexports.in"
                    className="text-sm text-[#2F4F3E] hover:underline"
                  >
                    sales@reliableexports.in
                  </a>
                </div>
              </div>

              <div className="mt-8">
                <Button
                  type="button"
                  className="bg-[#2F4F3E] hover:bg-[#233B2F] text-white rounded-md px-6 py-4 text-sm font-medium"
                  onClick={onRequestQuote}
                >
                  Request a Detailed Quote
                </Button>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white/90 rounded-lg border border-[#E3DED8] p-5 sm:p-6 shadow-sm">
              <h2 className=" text-lg sm:text-xl text-[#2B2B2B] mb-4">
                Send Us a Message
              </h2>

              {isSubmitted ? (
                <div className="py-8 text-center">
                  <p className=" text-lg text-[#2B2B2B] mb-2">
                    Thank you for reaching out.
                  </p>
                  <p className=" text-sm text-[#5A5A5A]">
                    We&apos;ve received your message and will respond shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="name"
                        className="text-xs sm:text-sm text-[#2B2B2B]"
                      >
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className="bg-white border-[#E3DED8] focus:border-[#2F4F3E] focus:ring-[#2F4F3E]/20 rounded-md text-sm"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label
                        htmlFor="company"
                        className="text-xs sm:text-sm text-[#2B2B2B]"
                      >
                        Company
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Acme Inc."
                        className="bg-white border-[#E3DED8] focus:border-[#2F4F3E] focus:ring-[#2F4F3E]/20 rounded-md text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label
                      htmlFor="email"
                      className="text-xs sm:text-sm text-[#2B2B2B]"
                    >
                      Work Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className="bg-white border-[#E3DED8] focus:border-[#2F4F3E] focus:ring-[#2F4F3E]/20 rounded-md text-sm"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label
                      htmlFor="message"
                      className="text-xs sm:text-sm text-[#2B2B2B]"
                    >
                      How can we help?
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Share details on bag types, quantities, markets and timelines..."
                      rows={4}
                      className="bg-white border-[#E3DED8] focus:border-[#2F4F3E] focus:ring-[#2F4F3E]/20 rounded-md resize-none text-sm"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#2F4F3E] hover:bg-[#233B2F] text-white rounded-md py-4 text-sm font-medium"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>

                  <p className=" text-[11px] text-[#5A5A5A] text-center">
                    By submitting, you agree to our privacy policy.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

